import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import './Drawer.css';

export interface DrawerProps {
  /**
   * Whether the drawer is currently visible
   */
  isOpen: boolean;
  /**
   * Callback when the drawer is closed
   */
  onClose: () => void;
  /**
   * Heading text displayed in the drawer header
   */
  heading?: string;
  /**
   * Content to render inside the drawer
   */
  children: ReactNode;
  /**
   * Width of the drawer (can be number in px or CSS string)
   * @default 400
   */
  width?: string | number;
  /**
   * Side of the screen where the drawer appears
   * @default 'right'
   */
  position?: 'left' | 'right';
  /**
   * Whether to show a darkened overlay behind the drawer
   * @default true
   */
  showOverlay?: boolean;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

const timeoutDuration = 300;

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"], iframe, audio[controls], video[controls]';

export function Drawer({
  isOpen,
  onClose,
  heading = 'Help',
  children,
  width = 400,
  position = 'right',
  showOverlay = true,
  className = '',
  'data-testid': dataTestId,
}: DrawerProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  const restoreFocus = useCallback(() => {
    const previouslyFocusedElement = previouslyFocusedElementRef.current;

    if (previouslyFocusedElement && document.contains(previouslyFocusedElement)) {
      previouslyFocusedElement.focus();
    }

    previouslyFocusedElementRef.current = null;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, timeoutDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else if (!shouldRender) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      if (!isOpen && !shouldRender) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (isOpen) {
      const activeElement = document.activeElement;
      previouslyFocusedElementRef.current = activeElement instanceof HTMLElement ? activeElement : null;
    } else {
      restoreFocus();
    }
  }, [isOpen, restoreFocus]);

  useEffect(() => restoreFocus, [restoreFocus]);

  useEffect(() => {
    if (isOpen && shouldRender && !isClosing) {
      drawerRef.current?.focus();
    }
  }, [isOpen, shouldRender, isClosing]);

  useEffect(() => {
    if (!isOpen || isClosing) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isClosing, onClose]);

  const getFocusableElements = useCallback(() => {
    if (!drawerRef.current) return [];

    return Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
      const isDisabled = element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true';
      const isHiddenInput = element.matches('input[type="hidden"]');
      const isHiddenByStyle =
        ['none', 'hidden'].includes(getComputedStyle(element).display) ||
        getComputedStyle(element).visibility === 'hidden';
      const isHidden =
        element.hidden || isHiddenByStyle || element.closest('[hidden], [inert], [aria-hidden="true"]') !== null;

      return !isDisabled && !isHiddenInput && element.tabIndex >= 0 && !isHidden;
    });
  }, []);

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;

    e.stopPropagation();

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) {
      e.preventDefault();
      drawerRef.current?.focus();

      return;
    }

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

    if (e.shiftKey && (currentIndex <= 0 || currentIndex === -1)) {
      e.preventDefault();
      focusableElements[focusableElements.length - 1].focus();
    } else if (!e.shiftKey && (currentIndex === -1 || currentIndex === focusableElements.length - 1)) {
      e.preventDefault();
      focusableElements[0].focus();
    }
  };

  if (!shouldRender) return null;

  const drawerWidth = typeof width === 'number' ? `${width}px` : width;
  const drawerPosition = position === 'left' ? { left: 0 } : { right: 0 };
  const baseTestId = dataTestId ?? 'drawer';

  return (
    <>
      {showOverlay && (
        <div
          aria-hidden='true'
          className={`drawer-overlay ${isClosing ? 'closing' : ''}`}
          data-testid={`${baseTestId}-overlay`}
          onClick={onClose}
        />
      )}
      <div
        ref={drawerRef}
        aria-labelledby={heading ? 'drawer-title' : undefined}
        aria-modal='true'
        className={`drawer ${isClosing ? 'closing' : ''} ${className}`}
        data-testid={baseTestId}
        role='dialog'
        style={{
          width: drawerWidth,
          ...drawerPosition,
        }}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}>
        <div
          className='drawer-header'
          data-testid={`${baseTestId}-header`}>
          {heading && (
            <h2
              className='drawer-title'
              data-testid={`${baseTestId}-title`}
              id='drawer-title'>
              {heading}
            </h2>
          )}
          <button
            aria-label='Close drawer'
            className='drawer-close'
            data-testid={`${baseTestId}-close`}
            type='button'
            onClick={onClose}>
            ×
          </button>
        </div>
        <div
          className='drawer-content'
          data-testid={`${baseTestId}-content`}>
          {children}
        </div>
      </div>
    </>
  );
}
